import styles from './FavoritesRow.module.css'
import { StationDetailsCopy } from '../copy'

export interface FavoritesRowProps {
  isFavorite: boolean
  onTap: () => void
  /** "Favourite" label (favourited). @defaultValue {@link StationDetailsCopy.IsFavorite} */
  favoriteLabel?: string
  /** "Add to favourites" label. @defaultValue {@link StationDetailsCopy.IsNotFavorite} */
  notFavoriteLabel?: string
}

/**
 * Favourites toggle — Figma SD/FavoritesRow (6970:29529). Heart-circle
 * glyph (`heartCircle` / `heartCircleFilled`, the same circular-backed
 * asset iOS bundles, 36×36) + label (`regularPx16` / text-primary).
 * a11y id `station-details-favorite-{isFavorite}` (spec §7).
 */
export function FavoritesRow({
  isFavorite,
  onTap,
  favoriteLabel = StationDetailsCopy.IsFavorite,
  notFavoriteLabel = StationDetailsCopy.IsNotFavorite,
}: FavoritesRowProps): React.ReactElement {
  const label = isFavorite ? favoriteLabel : notFavoriteLabel
  return (
    <button
      type="button"
      onClick={onTap}
      data-testid={`station-details-favorite-${isFavorite}`}
      className={styles.row}
    >
      <FavoriteIcon isFavorite={isFavorite} className={styles.icon} />
      {/* Test-only seam — preserves the assertion that toggles on
          isFavorite without coupling to the rendered SVG XML. */}
      <span data-testid="favorites-row-icon" data-filled={String(isFavorite)} hidden />
      <span className={styles.label}>{label}</span>
    </button>
  )
}

function FavoriteIcon({
  isFavorite,
  className,
}: {
  isFavorite: boolean
  className?: string
}): React.ReactElement {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <rect width="36" height="36" rx="18" className={styles.iconBackground} />
      <path
        d={
          isFavorite
            ? 'M11 16.2239C11 13.7357 12.7604 12 15.0268 12C16.3435 12 17.3831 12.6034 18 13.4915C18.6238 12.5967 19.6564 12 20.9732 12C23.2465 12 25 13.7357 25 16.2239C25 19.1257 22.5188 21.9869 18.6445 24.4277C18.4366 24.5566 18.1802 24.6853 18 24.6853C17.8267 24.6853 17.5633 24.5566 17.3555 24.4277C13.4812 21.9869 11 19.1257 11 16.2239Z'
            : 'M11 16.2239C11 13.7357 12.7604 12 15.0268 12C16.3435 12 17.3831 12.6034 18 13.4915C18.6238 12.5967 19.6564 12 20.9732 12C23.2465 12 25 13.7357 25 16.2239C25 19.1257 22.5188 21.9869 18.6445 24.4277C18.4366 24.5566 18.1802 24.6853 18 24.6853C17.8267 24.6853 17.5633 24.5566 17.3555 24.4277C13.4812 21.9869 11 19.1257 11 16.2239ZM12.3931 16.2239C12.3931 18.6241 14.9505 21.2751 17.8683 23.1734C17.9307 23.2141 17.9723 23.2412 18 23.2412C18.0277 23.2412 18.0693 23.2141 18.1317 23.1734C21.0495 21.2751 23.6069 18.6241 23.6069 16.2239C23.6069 14.5018 22.4495 13.3424 20.897 13.3424C19.6495 13.3424 18.9703 14.0949 18.5059 14.7391C18.298 15.0035 18.1802 15.0985 18 15.0985C17.8198 15.0985 17.6881 15.0103 17.494 14.7391C17.0574 14.0882 16.3574 13.3424 15.103 13.3424C13.5505 13.3424 12.3931 14.5018 12.3931 16.2239Z'
        }
        className={isFavorite ? styles.iconHeartSelected : styles.iconHeart}
      />
    </svg>
  )
}
